import { BriefcaseBusiness, ChevronDown, Hash } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

const CAP_RATE = 0.0475

function parseNum(v: string | number): number {
	return parseFloat(String(v).replace(/[^0-9.]/g, '')) || 0
}

/** `ch` width that hugs the value — narrow glyphs (. and ,) count ~half a
 * digit so the input doesn't leave a gap before the trailing $ / %. */
function chWidth(v: string): string {
	const w = [...v].reduce((a, c) => a + (c === '.' || c === ',' ? 0.5 : 1), 0)
	return `${Math.max(w, 1)}ch`
}

function money(x: number): string {
	return '$' + Math.round(x).toLocaleString('en-US')
}

function moneyM(x: number): string {
	if (x >= 1_000_000)
		return (
			'$' +
			(x / 1_000_000).toFixed(x / 1_000_000 >= 10 ? 0 : 1).replace(/\.0$/, '') +
			'M'
		)
	if (x >= 1_000) return '$' + Math.round(x / 1_000) + 'K'
	return '$' + Math.round(x)
}

/** Dark-to-light fill for a range track, filled to the value's position. */
function trackBg(value: number, min: number, max: number) {
	const pct = Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100))
	return {
		background: `linear-gradient(to right, #322F2F 0%, #322F2F ${pct}%, #F3F3F3 ${pct}%, #F3F3F3 100%)`,
	}
}

// White thumb with a soft ring, on the dark-fill track set via inline style.
const sliderClass =
	'w-full h-3 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-[#32302F] [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-[#32302F]'

// Soft inset (neumorphic) effect: a dark shadow carved into the top-left inner
// edge and a white highlight on the bottom-right, so an element reads as gently
// pressed into the #f7f7f7 background. Reused by every card/box on this page.
const insetShadow =
	'shadow-[inset_3px_3px_8px_#0000000D,inset_-3px_-3px_8px_#FFFFFF]'

const cardClass = `rounded-[30px] p-6 border border-[#FFFFFF] bg-[#FFFFFF1A] ${insetShadow}`

/** Panel title with a pastel icon square — "Your Portfolio" / "Your Numbers". */
function SectionHeader({
	variant,
	title,
}: {
	variant: 'portfolio' | 'numbers'
	title: string
}) {
	const isPortfolio = variant === 'portfolio'
	return (
		<div className="mb-4 flex items-center gap-3">
			<span
				className={`flex size-9 items-center justify-center rounded-lg ${
					isPortfolio ? 'bg-[#D6E5BE40]' : 'bg-[#DCCCEA66]'
				}`}
			>
				{isPortfolio ? (
					<BriefcaseBusiness
						strokeWidth={1.25}
						className="size-4.5 text-[#A5BB83]"
					/>
				) : (
					<Hash strokeWidth={1.25} className="size-4.5 text-[#9976B8]" />
				)}
			</span>
			<span className="text-[15px] font-medium text-[#322F2F] capitalize">
				{title}
			</span>
		</div>
	)
}

/** Bordered "[ value │ unit ]" pill sitting to the right of a slider label. */
function ValueBox({ value, unit }: { value: string; unit: string }) {
	return (
		<div className="flex w-35 shrink-0 items-center justify-between overflow-hidden rounded-xl border border-[#F0F0F0] bg-[#F7F7F7] p-3">
			<span className="text-[13px] leading-none text-[#32302F] tabular-nums">
				{value}
			</span>
			<span className="text-[13px] leading-none text-[#32302F]">{unit}</span>
		</div>
	)
}

function SliderField({
	label,
	id,
	min,
	max,
	step,
	value,
	onChange,
	valueText,
	unit,
}: {
	label: string
	id: string
	min: number
	max: number
	step: number
	value: number
	onChange: (v: number) => void
	valueText: string
	unit: string
}) {
	return (
		<div
			className={`mb-4 rounded-[15px] border border-[#FFFFFF] bg-[#FFFFFF1A] p-5 py-3 ${insetShadow}`}
		>
			<div className="mb-2 flex items-center justify-between gap-3">
				<span className="text-[15px] font-medium text-[#322F2F] capitalize">
					{label}
				</span>
				<ValueBox value={valueText} unit={unit} />
			</div>
			<input
				id={id}
				type="range"
				min={min}
				max={max}
				step={step}
				value={value}
				onChange={(e) => onChange(parseFloat(e.target.value))}
				style={trackBg(value, min, max)}
				className={sliderClass}
			/>
		</div>
	)
}

/** Rounded box with the label floating at the top and the value below. */
function FloatingField({
	label,
	children,
}: {
	label: React.ReactNode
	children: React.ReactNode
}) {
	return (
		// fieldset/legend so the top border is GENUINELY cut where the label sits
		// (a real gap, not a bg cover) — the label keeps its translucent glass bg
		// and no border shows under it.
		<fieldset className="w-full min-w-0 rounded-[15px] border border-[#F0F0F0] bg-[#FFFFFF1A] px-5 pb-2">
			<legend className="bg-[#FFFFFF1A] text-[15px] font-normal text-[#747474] capitalize">
				{label}
			</legend>
			{children}
		</fieldset>
	)
}

function MoneyInput({
	label,
	value,
	onChange,
	prefix,
	suffix,
}: {
	label: string
	value: string
	onChange: (v: string) => void
	prefix?: string
	suffix?: string
}) {
	const ref = useRef<HTMLInputElement>(null)
	return (
		<FloatingField label={label}>
			<div className="flex items-center">
				{prefix && <span className="text-[16px] text-[#484848]">{prefix}</span>}
				<input
					ref={ref}
					type="text"
					inputMode="numeric"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onBlur={() => {
						const n = parseNum(value)
						onChange(
							prefix
								? n.toLocaleString('en-US')
								: suffix
									? n + suffix
									: String(n),
						)
					}}
					className="min-w-0 flex-1 bg-transparent text-[16px] text-[#484848] tabular-nums outline-none placeholder:font-light placeholder:text-[#CCCCCC]"
				/>
				{suffix && <span className="text-[16px] text-[#484848]">{suffix}</span>}
			</div>
		</FloatingField>
	)
}

/** One screening-assumption row: label left, small value box right. */
function AdvRow({
	label,
	value,
	onChange,
	prefix,
	suffix,
}: {
	label: string
	value: string
	onChange: (v: string) => void
	prefix?: string
	suffix?: string
}) {
	return (
		<div
			className={`flex items-center justify-between gap-3 rounded-[15px] border border-[#FFFFFF] bg-[#FFFFFF1A] p-5 py-3 ${insetShadow}`}
		>
			<span className="text-[15px] font-medium text-[#322F2F] capitalize">
				{label}
			</span>
			<div className="flex w-25 shrink-0 items-center justify-end gap-0.5 overflow-hidden rounded-xl border border-[#F0F0F0] bg-[#F7F7F7] px-3 py-2.5 text-[13px] leading-none text-[#32302F]">
				{prefix && <span className="shrink-0">{prefix}</span>}
				<input
					type="text"
					inputMode="numeric"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onBlur={() => {
						const n = parseNum(value)
						onChange(
							prefix
								? n.toLocaleString('en-US')
								: suffix
									? n + suffix
									: String(n),
						)
					}}
					// fontFamily:inherit so the number uses the SAME font as the $/%
					// (inputs default to the browser font → baselines don't match);
					// width sized to the value so the group centers cleanly.
					style={{ width: chWidth(value), fontFamily: 'inherit' }}
					className="border-0 bg-transparent p-0 text-left text-[13px] leading-none tabular-nums outline-none"
				/>
				{suffix && <span className="shrink-0">{suffix}</span>}
			</div>
		</div>
	)
}

/** One "what you get back" card: label + big value, optional hint. */
function ResultRow({
	label,
	value,
	hint,
	valueClass = 'text-[#322F2F]',
}: {
	label: string
	value: string
	hint?: string
	valueClass?: string
}) {
	return (
		<div
			className={`rounded-[15px] border border-[#ffffff] bg-[#FFFFFF1A] p-3 ${insetShadow}`}
		>
			<div className="flex items-baseline justify-between gap-3">
				<span className="text-[13px] leading-4 font-medium text-[#32302F] capitalize">
					{label}
				</span>
				<span className={`text-lg font-medium ${valueClass}`}>{value}</span>
			</div>
			{hint && (
				<p className="mt-1 text-[12px] leading-4 font-medium text-[#32302f] capitalize">
					{hint}
				</p>
			)}
		</div>
	)
}

export default function CostCalculatorRoute() {
	const [units, setUnits] = useState(1000)
	const [turnover, setTurnover] = useState(15)
	const [vacancy, setVacancy] = useState(3)
	const [rent, setRent] = useState('2,000')
	const [badDebtMode, setBadDebtMode] = useState<'amt' | 'pct'>('amt')
	const [badDebt, setBadDebt] = useState('50,000')
	const [reduction, setReduction] = useState(50)
	const [advOpen, setAdvOpen] = useState(false)

	const [appsPerVacancy, setAppsPerVacancy] = useState('7')
	const [applicantsPerApp, setApplicantsPerApp] = useState('2')
	const [fullScreenPct, setFullScreenPct] = useState('20')
	const [prePrice, setPrePrice] = useState('10')
	const [fullPrice, setFullPrice] = useState('35')

	const annualRent = parseNum(rent) * units * 12

	const currentBadDebt =
		badDebtMode === 'pct'
			? (parseNum(badDebt) / 100) * annualRent
			: parseNum(badDebt)

	const vacancies = units * (turnover / 100)
	const applications = vacancies * parseNum(appsPerVacancy)
	const applicants = applications * parseNum(applicantsPerApp)
	const delta = Math.max(parseNum(fullPrice) - parseNum(prePrice), 0)
	const blendedCost =
		parseNum(prePrice) + (parseNum(fullScreenPct) / 100) * delta
	const yearlyCost = applicants * blendedCost
	const monthlyCost = yearlyCost / 12
	const badDebtRecovered = currentBadDebt * (reduction / 100)
	const realEstateValue = badDebtRecovered / CAP_RATE
	const netValue = badDebtRecovered + realEstateValue - yearlyCost

	const badHint =
		badDebtMode === 'amt'
			? `≈ ${annualRent > 0 ? ((currentBadDebt / annualRent) * 100).toFixed(1) : '0'}% of your rent`
			: `= ${money(currentBadDebt)} / year`

	const switchMode = useCallback(
		(mode: 'amt' | 'pct') => {
			if (mode === badDebtMode) return
			if (mode === 'pct') {
				setBadDebt(
					annualRent > 0
						? ((currentBadDebt / annualRent) * 100).toFixed(1)
						: '0',
				)
			} else {
				setBadDebt(Math.round(currentBadDebt).toLocaleString('en-US'))
			}
			setBadDebtMode(mode)
		},
		[badDebtMode, annualRent, currentBadDebt],
	)

	return (
		<div className="min-h-screen w-full bg-[#f7f7f7] pb-20">
			<div className="xl:px-o mx-auto max-w-[1150px] px-5 pt-12">
				{/* Header */}
				<h1 className="font-calgary mb-3 text-[51px] leading-12 font-medium tracking-tight text-[#322F2F]">
					What TrueTenant costs.
					<br />
					And what it saves you.
				</h1>
				<p className="mb-8 text-[15px] leading-4 font-medium text-[#32302F] capitalize">
					Enter your portfolio. We price screening automatically, then show the
					<br />
					bad debt you recover and the real estate value it builds.
				</p>

				{/* Two-column grid */}
				<div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[6fr_4fr]">
					{/* ── INPUTS PANEL ── */}
					<div className={cardClass}>
						<SectionHeader variant="portfolio" title="Your Portfolio" />

						<SliderField
							label="Portfolio size"
							id="units"
							min={10}
							max={10000}
							step={10}
							value={units}
							onChange={setUnits}
							valueText={units.toLocaleString('en-US')}
							unit="Units"
						/>
						<SliderField
							label="Annual turnover rate"
							id="turnover"
							min={5}
							max={80}
							step={1}
							value={turnover}
							onChange={setTurnover}
							valueText={`${turnover}`}
							unit="%"
						/>
						<SliderField
							label="Vacancy rate"
							id="vacancy"
							min={0}
							max={20}
							step={0.5}
							value={vacancy}
							onChange={setVacancy}
							valueText={`${vacancy}`}
							unit="%"
						/>

						<div
							className={`mb-4 rounded-[15px] border border-[#FFFFFF] bg-[#FFFFFF1A] p-5 py-3 ${insetShadow}`}
						>
							<MoneyInput
								label="Average monthly rent"
								value={rent}
								onChange={setRent}
								prefix="$"
							/>
						</div>

						{/* Bad debt with toggle */}
						<div
							className={`mb-4 rounded-[15px] border border-[#FFFFFF] bg-[#FFFFFF1A] p-5 py-3 ${insetShadow}`}
						>
							<div className="mb-3 inline-flex items-center gap-1 rounded-full border border-[#F0F0F0] bg-[#F7F7F7] p-1">
								{(['amt', 'pct'] as const).map((m) => (
									<button
										key={m}
										type="button"
										onClick={() => switchMode(m)}
										className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all ${
											badDebtMode === m
												? 'bg-[#322F2F] text-white'
												: 'text-[#747474]'
										}`}
									>
										{m === 'amt' ? '$ / Year' : '% Of Rent'}
									</button>
								))}
							</div>
							<FloatingField label="Current bad debt / year">
								<div className="flex items-center gap-0.5">
									{badDebtMode === 'amt' && (
										<span className="text-[16px] text-[#484848]">$</span>
									)}
									<input
										type="text"
										inputMode="numeric"
										value={badDebt}
										onChange={(e) => setBadDebt(e.target.value)}
										onBlur={() => {
											const n = parseNum(badDebt)
											setBadDebt(
												badDebtMode === 'pct'
													? n.toFixed(1)
													: n.toLocaleString('en-US'),
											)
										}}
										// Size to the value so the % / $ sits right after the number
										// instead of flex-1 pushing it to the far right.
										style={{ width: chWidth(badDebt), fontFamily: 'inherit' }}
										className="min-w-0 bg-transparent text-[16px] text-[#484848] tabular-nums outline-none placeholder:font-light placeholder:text-[#CCCCCC]"
									/>
									{badDebtMode === 'pct' && (
										<span className="text-[16px] text-[#484848]">%</span>
									)}
								</div>
							</FloatingField>
							<p className="mt-1 text-xs text-[#32302F] capitalize">
								{badHint}
							</p>
						</div>

						{/* Advanced assumptions */}
						<div className="mt-4 border-t border-[#ECECEC] pt-5">
							<button
								type="button"
								onClick={() => setAdvOpen((o) => !o)}
								className="flex w-full items-center justify-between text-left text-[15px] font-medium text-[#322F2F] capitalize"
							>
								Screening assumptions
								<ChevronDown
									className={`size-5 text-[#747474] transition-transform duration-200 ${
										advOpen ? 'rotate-180' : ''
									}`}
								/>
							</button>
							{advOpen && (
								<div className="mt-4 space-y-3">
									<AdvRow
										label="Applications per vacancy"
										value={appsPerVacancy}
										onChange={setAppsPerVacancy}
									/>
									<AdvRow
										label="Applicants (adults) per application"
										value={applicantsPerApp}
										onChange={setApplicantsPerApp}
									/>
									<AdvRow
										label="Sent to full screen"
										value={fullScreenPct}
										onChange={setFullScreenPct}
										suffix="%"
									/>
									<AdvRow
										label="Pre-screen price"
										value={prePrice}
										onChange={setPrePrice}
										prefix="$"
									/>
									<AdvRow
										label="Full-screen price"
										value={fullPrice}
										onChange={setFullPrice}
										prefix="$"
									/>
								</div>
							)}
						</div>
					</div>

					{/* ── RESULTS PANEL ── */}
					<div className={cardClass}>
						<SectionHeader variant="numbers" title="Your Numbers" />

						{/* Monthly hero card */}
						<div
							className={`mb-4 rounded-[15px] border border-[#FFFFFF] bg-[#FFFFFF1A] p-5 py-3 ${insetShadow}`}
						>
							<p className="mb-2 text-[13px] leading-4 font-medium text-[#32302F] capitalize">
								TrueTenant, per month
							</p>
							<p className="mb-2 text-[50px] leading-12 font-medium tracking-tight text-[#322F2F]">
								{money(monthlyCost)}
							</p>
							<p className="text-[13px] leading-4 font-medium text-[#32302F] capitalize">
								<span>{money(yearlyCost)}</span> per year
							</p>
						</div>

						{/* Stats row */}
						<div className="mb-4 grid grid-cols-2 gap-3">
							<div
								className={`rounded-[15px] border border-[#FFFFFF] bg-[#FFFFFF1A] p-5 py-3 ${insetShadow}`}
							>
								<p className="mb-1 text-[30px] leading-none font-semibold text-[#322F2F]">
									{Math.round(applicants).toLocaleString('en-US')}
								</p>
								<p className="text-[13px] leading-4 font-medium text-[#32302F] capitalize">
									Applicants screened / yr
								</p>
							</div>
							<div
								className={`rounded-[15px] border border-[#FFFFFF] bg-[#FFFFFF1A] p-5 py-3 ${insetShadow}`}
							>
								<p className="mb-1 text-[30px] leading-none font-semibold text-[#322F2F]">
									${blendedCost.toFixed(2)}
								</p>
								<p className="text-[13px] leading-4 font-medium text-[#32302F] capitalize">
									Blended cost/applicant
									<br />
									<span className="text-[8px] capitalize">
										(80% pre-screen, 20% full)
									</span>
								</p>
							</div>
						</div>

						{/* What you get back — stacked cards */}
						<div
							className={`rounded-[15px] border border-[#FFFFFF] bg-[#FFFFFF1A] p-5 py-3 ${insetShadow}`}
						>
							<p className="mb-4 text-[15px] leading-4 font-medium text-[#32302F] capitalize">
								What you get back
							</p>

							<div className="space-y-3">
								{/* Reduction slider */}
								<div
									className={`rounded-[15px] border border-[#FFFFFF] bg-[#FFFFFF1A] p-3 ${insetShadow}`}
								>
									<div className="mb-3 flex items-center justify-between gap-3">
										<span className="text-[13px] leading-4 font-medium text-[#32302F] capitalize">
											Assume TrueTenant cuts bad debt by
										</span>
										<span className="text-base font-medium text-[#322F2F]">
											{reduction}%
										</span>
									</div>
									<input
										type="range"
										min={0}
										max={100}
										step={5}
										value={reduction}
										onChange={(e) => setReduction(Number(e.target.value))}
										style={trackBg(reduction, 0, 100)}
										className={sliderClass}
									/>
								</div>

								<ResultRow
									label="Bad debt recovered / year"
									value={money(badDebtRecovered)}
									hint="Recurring rent you stop losing, every year."
								/>

								<ResultRow
									label="Real estate value it builds"
									value={moneyM(realEstateValue)}
									hint="Every dollar of recovered NOI is equity you can refinance into your next acquisition."
								/>

								{/* Net value — emphasized */}
								<div
									className={`rounded-[15px] border border-[#ffffff] bg-[#FFFFFF1A] p-3 ${insetShadow}`}
								>
									<span className="text-[13px] leading-4 font-medium text-[#32302F] capitalize">
										Net value with TrueTenant
									</span>
									<p
										className={`mb-2 text-[50px] leading-12 font-medium tracking-tight text-[#322f2f] ${
											netValue >= 0 ? 'text-[#322F2F]' : 'text-[#DC2626]'
										}`}
									>
										{netValue < 0 ? '−' : ''}
										{money(Math.abs(netValue))}
									</p>
									<p className="text-[10px] text-[#322F2F] capitalize">
										Bad debt recovered {money(badDebtRecovered)} + real estate{' '}
										{money(realEstateValue)} − TrueTenant {money(yearlyCost)}
									</p>
								</div>
							</div>
						</div>

						<p className="mt-4 text-[10px] text-[#322F2F] capitalize">
							The bad debt reduction % is a scenario you set, not a TrueTenant
							guarantee. Real estate value uses a 4.75% cap rate (CBRE Canadian
							multifamily). Year-one figure.
						</p>
					</div>
				</div>
			</div>
		</div>
	)
}
